import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { formatDateTimeShort } from '../../utils/dateFormat';
import axios from 'axios';
import PageTitle from '../../components/PageTitle';
import {
  Receipt,
  Search,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  ArrowDownUp,
  Users,
} from 'lucide-react';

interface OrderUser {
  _id: string;
  email: string;
}

interface OrderMetadata {
  context?: {
    type?: string;
    tier: string;
    planId: string;
  } | null;
  paidWithBalance?: boolean;
}

interface Order {
  _id: string;
  user: OrderUser | null;
  amount: number;
  currency: string;
  provider: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  paymentId?: string;
  description?: string;
  type: 'user_recharge' | 'admin_recharge';
  admin?: OrderUser | null;
  adminReason?: string;
  metadata?: OrderMetadata;
  createdAt: string;
}

interface OrderStats {
  totalOrders: number;
  totalRevenue: number;
  completedOrders: number;
  averageOrderValue: number;
  byStatus: Record<string, number>;
  byProvider: Record<string, number>;
  topSpenders: Array<{
    _id: string;
    totalSpent: number;
    orderCount: number;
    email: string;
  }>;
}

const STATUS_ICONS: Record<string, typeof CheckCircle> = {
  completed: CheckCircle,
  pending: Clock,
  failed: XCircle,
  cancelled: AlertTriangle,
  refunded: ArrowDownUp,
};

const STATUS_COLORS: Record<string, string> = {
  completed: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  pending: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  failed: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  cancelled: 'bg-muted text-muted-foreground',
  refunded: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
};

export default function DashboardOrders() {
  const { t } = useTranslation('dashboard');
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        status: statusFilter,
        provider: providerFilter,
        type: typeFilter,
      });
      if (searchDebounced.trim()) params.set('search', searchDebounced.trim());

      const { data } = await axios.get(`/api/admin/orders?${params}`);
      setOrders(data.orders);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch {
      toast.error(t('orders.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, providerFilter, typeFilter, searchDebounced, t]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/admin/orders/stats');
      setStats(data);
    } catch {
      toast.error(t('orders.stats_failed'));
    }
  }, [t]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, providerFilter, typeFilter, searchDebounced]);

  const formatCurrency = (amount: number, currency: string) => {
    const upper = currency.toUpperCase();
    return upper === 'USD' ? `$${amount.toFixed(2)}` : `${upper} ${amount.toFixed(2)}`;
  };

  const formatOrderDescription = (order: Order) => {
    const context = order.metadata?.context;
    if (context?.type === 'subscription' && order.metadata?.paidWithBalance) {
      return t('orders.description_subscription_balance', {
        tier: t(`orders.tier_${context.tier}` as any),
        plan: t(`orders.plan_${context.planId}` as any),
      });
    }

    if (context?.type === 'subscription_shortfall') {
      return t('orders.description_subscription_shortfall', {
        tier: t(`orders.tier_${context.tier}` as any),
        plan: t(`orders.plan_${context.planId}` as any),
      });
    }

    if (context?.type === 'subscription') {
      return t('orders.description_subscription_provider', {
        provider: t(`orders.provider_${order.provider}` as any),
      });
    }

    const description = order.description?.trim();
    if (!description) return '—';
    return description;
  };

  const statCards = stats
    ? [
        { label: t('orders.total_orders'), value: stats.totalOrders, icon: Receipt },
        { label: t('orders.total_revenue'), value: `$${stats.totalRevenue.toFixed(2)}`, icon: DollarSign },
        { label: t('orders.completed_orders'), value: stats.completedOrders, icon: CheckCircle },
        {
          label: t('orders.avg_order_value'),
          value: `$${stats.averageOrderValue.toFixed(2)}`,
          icon: ArrowDownUp,
        },
      ]
    : [];

  return (
    <div className="p-8 space-y-6">
      <PageTitle titleKey="orders.title" namespace="dashboard" descriptionKey="orders.subtitle" />
      {/* Header */}
      <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">
          {t('orders.title')}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{t('orders.subtitle')}</p>
      </div>

      {/* Stat cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((card) => (
            <div
              key={card.label}
              className="bg-card border border-border rounded-xl p-5 flex items-center gap-4"
            >
              <div className="p-2.5 rounded-lg bg-primary/10">
                <card.icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {card.label}
                </p>
                <p className="text-xl font-bold text-foreground mt-0.5">{card.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('orders.search_placeholder')}
            className="w-full pl-9 pr-4 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="all">{t('orders.all_statuses')}</option>
          <option value="pending">{t('orders.status_pending')}</option>
          <option value="completed">{t('orders.status_completed')}</option>
          <option value="failed">{t('orders.status_failed')}</option>
          <option value="cancelled">{t('orders.status_cancelled')}</option>
          <option value="refunded">{t('orders.status_refunded')}</option>
        </select>
        <select
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
          className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="all">{t('orders.all_providers')}</option>
          <option value="alipay">{t('orders.provider_alipay')}</option>
          <option value="wechat_pay">{t('orders.provider_wechat_pay')}</option>
          <option value="stripe">{t('orders.provider_stripe')}</option>
          <option value="paypal">{t('orders.provider_paypal')}</option>
          <option value="none">{t('orders.provider_none')}</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="all">{t('orders.all_types')}</option>
          <option value="user_recharge">{t('orders.type_user_recharge')}</option>
          <option value="admin_recharge">{t('orders.type_admin_recharge')}</option>
        </select>
      </div>

      {/* Orders table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('orders.col_user')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('orders.col_amount')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('orders.col_provider')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('orders.col_status')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('orders.col_type')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('orders.col_description')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('orders.col_date')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <Receipt className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="font-medium text-foreground">{t('orders.no_orders')}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t('orders.no_orders_desc')}</p>
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const StatusIcon = STATUS_ICONS[order.status] || Clock;
                  return (
                    <tr
                      key={order._id}
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        {order.user ? (
                          <div>
                            <p className="font-medium text-foreground">{order.user.email}</p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono font-medium text-foreground">
                        {formatCurrency(order.amount, order.currency)}
                      </td>
                      <td className="px-4 py-3 text-foreground">
                        {t(`orders.provider_${order.provider}` as any)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[order.status] || ''}`}
                        >
                          <StatusIcon className="h-3 w-3" />
                          {t(`orders.status_${order.status}` as any)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground">
                        {t(`orders.type_${order.type}` as any)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate">
                        {formatOrderDescription(order)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {formatDateTimeShort(order.createdAt)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30">
            <p className="text-xs text-muted-foreground">
              {t('orders.page_info', { page, totalPages })} &middot; {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                {t('orders.previous')}
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
              >
                {t('orders.next')}
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Top spenders */}
      {stats && stats.topSpenders.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold text-foreground">{t('orders.top_spenders')}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">#</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                    {t('orders.col_user')}
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                    {t('orders.col_total_spent')}
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                    {t('orders.col_order_count')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.topSpenders.map((spender, i) => (
                  <tr
                    key={spender._id}
                    className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-4 py-3 text-muted-foreground font-mono">{i + 1}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{spender.email}</p>
                    </td>
                    <td className="px-4 py-3 font-mono font-medium text-foreground">
                      ${spender.totalSpent.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-foreground">{spender.orderCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
